import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  alpha,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Divider,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
  LocationCity as LocationCityIcon,
  AccountBalance as AccountBalanceIcon,
  Notes as NotesIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  Rule as RuleIcon,
  HomeWork as HomeWorkIcon,
  AccessTime as AccessTimeIcon,
  Apartment as ApartmentIcon,
  Description as DescriptionIcon,
  Engineering as EngineeringIcon,
  Gavel as GavelIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Project, Permit, Document as DocumentType } from '../../types';

interface ProjectOverviewProps {
  project: Project;
  permits: Permit[];
  documentsWithReports: Set<string>;
  isAnalyzingDocument: string | null;
  handleRunDocumentCompliance: (document: DocumentType, permitId: string) => void;
  handleViewComplianceReport: (documentId: string) => void;
}

const ProjectOverview: React.FC<ProjectOverviewProps> = ({
  project,
  permits,
  documentsWithReports,
  isAnalyzingDocument,
  handleRunDocumentCompliance,
  handleViewComplianceReport,
}) => {
  const theme = useTheme();

  const firstPermitDate = permits.length > 0 
    ? new Date(permits[0].submissionDate).toLocaleDateString()
    : 'Not started';

  const statusChip = project.status ? (
    <Chip
      label={project.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      color={
        project.status === 'completed' ? 'success' :
        project.status === 'in_progress' ? 'primary' : 'default'
      }
      size="small"
      sx={{ 
        borderRadius: '100px',
        '& .MuiChip-label': {
          px: 1,
          fontWeight: 500,
        }
      }}
    />
  ) : null;

  // Create a function to render city codes as clickable links
  const renderCityCodes = (codeUrls: string[] | undefined) => {
    if (!codeUrls || codeUrls.length === 0) return null;
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {codeUrls.map((url, index) => {
          let displayName;
          
          try {
            // Try to extract a name from the URL
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/').filter(Boolean);
            displayName = pathParts[pathParts.length - 1] || urlObj.hostname;
            
            // Clean up the display name if it's a filename
            displayName = displayName
              .replace(/[-_]/g, ' ')
              .replace(/\.[^/.]+$/, '') // Remove file extension
              .replace(/\b\w/g, c => c.toUpperCase()); // Capitalize first letter of each word
          } catch (error) {
            // If URL parsing fails, use the URL string itself or a fallback
            console.error("Invalid URL in city codes:", url);
            displayName = url.length > 30 ? `${url.substring(0, 30)}...` : url;
          }
          
          return (
            <Chip
              key={index}
              label={displayName}
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          );
        })}
      </Box>
    );
  };

  // Create a comprehensive list of all project fields with their icons and labels
  const allProjectFields = [
    { key: 'projectID', icon: <DescriptionIcon />, label: 'Project ID', value: project.projectID },
    { key: 'ownerID', icon: <PersonIcon />, label: 'Project Owner', value: project.ownerID },
    { key: 'projectType', icon: <HomeWorkIcon />, label: 'Project Type', value: project.projectType },
    { key: 'permitType', icon: <EngineeringIcon />, label: 'Permit Type', value: project.permitType },
    { key: 'description', icon: <NotesIcon />, label: 'Description', value: project.description },
    { key: 'address', icon: <LocationCityIcon />, label: 'Address', value: project.address },
    { key: 'estimatedCost', icon: <AccountBalanceIcon />, label: 'Estimated Cost', value: project.estimatedCost ? `$${project.estimatedCost.toLocaleString()}` : undefined },
    { key: 'size', icon: <ApartmentIcon />, label: 'Size', value: project.size },
    { key: 'startDate', icon: <CalendarTodayIcon />, label: 'Start Date', value: project.startDate },
    { key: 'endDate', icon: <CalendarTodayIcon />, label: 'End Date', value: project.endDate },
    { key: 'cityCodes', icon: <GavelIcon />, label: 'City Codes', value: project.cityCodes && project.cityCodes.length > 0 ? renderCityCodes(project.cityCodes) : undefined }
  ];

  const projectInfo = allProjectFields.filter(field => field.value !== undefined && field.value !== null);

  const complianceDocuments = permits
    .flatMap(permit => permit.documents || [])
    .map(doc => ({
      name: doc.name,
      status: doc.status,
      date: new Date(doc.uploadDate).toLocaleDateString(),
    }))
    .slice(0, 5);

  return (
    <Grid container spacing={3}>
      {/* Left Column - Project Details */}
      <Grid item xs={12} md={6}>
        <Grid container spacing={3}>
          {/* Project Details Card */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: '16px' }}>
              <CardHeader
                title="Project Details"
                action={statusChip}
                sx={{
                  p: 3,
                  pb: 0,
                  '& .MuiCardHeader-title': {
                    fontSize: '1.25rem',
                    fontWeight: 600,
                  },
                  '& .MuiCardHeader-action': {
                    m: 0,
                    alignSelf: 'center',
                  },
                }}
              />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {projectInfo.map((info, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        '&:last-child': { mb: 0 },
                      }}
                    >
                      <Box sx={{ color: 'text.primary' }}>
                        {info.icon}
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {info.label}
                        </Typography>
                        {React.isValidElement(info.value) ? (
                          info.value
                        ) : (
                          <Typography variant="body1">
                            {info.value}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* Right Column - Document Compliance and Recent Documents */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ 
          p: 3, 
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Compliance Checks</Typography>
          </Box>

          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell width="40%">Document Name</TableCell>
                  <TableCell width="30%">Plan Type</TableCell>
                  <TableCell align="center" width="15%">Run Check</TableCell>
                  <TableCell align="center" width="15%">Report</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permits.flatMap(permit =>
                  (permit.documents || []).map(document => (
                    <TableRow
                      key={document.documentID}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell sx={{ maxWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InsertDriveFileIcon sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {document.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {permit.permitType}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRunDocumentCompliance(document, permit.permitID);
                          }}
                          disabled={isAnalyzingDocument === document.documentID}
                          sx={{ minWidth: 'auto', p: 1 }}
                        >
                          {isAnalyzingDocument === document.documentID ? (
                            <CircularProgress size={20} />
                          ) : (
                            <RuleIcon />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell align="center">
                        {documentsWithReports.has(document.documentID) ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewComplianceReport(document.documentID);
                            }}
                            sx={{
                              bgcolor: theme.palette.success.main,
                              '&:hover': {
                                bgcolor: theme.palette.success.dark,
                              },
                              minWidth: 'auto',
                              p: 1,
                            }}
                          >
                            <VisibilityIcon />
                          </Button>
                        ) : (
                          <Box sx={{ height: 32 }} /> // Placeholder for alignment
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {/* Recent Documents Card moved here */}
        <Card sx={{ borderRadius: '16px', mt: 3 }}>
          <CardHeader
            title="Recent Documents"
            sx={{
              p: 3,
              pb: 0,
              '& .MuiCardHeader-title': {
                fontSize: '1.25rem',
                fontWeight: 600,
              },
            }}
          />
          <CardContent sx={{ p: 3 }}>
            {complianceDocuments.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {complianceDocuments.map((doc, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 1,
                    }}
                  >
                    <InsertDriveFileIcon sx={{ color: 'primary.main', mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {doc.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last Updated: {doc.date}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recent documents found.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ProjectOverview; 